import React, { useEffect, useMemo, useState } from 'react';
import { Box, Collapse, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import Card from "../../common/Card";
import Table from "../../common/CustomTable";
import Button from "../../common/Button";
import { SearchOutlined, ExitToApp, KeyboardArrowDown, DownloadOutlined, CloudUpload, PictureAsPdfOutlined } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import CustomTextField from '../../common/CustomTextField';
import { useAppDispatch } from '../../../redux/slice/redux-hooks';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { selectIsValueFoundInToken, useAuthSlice } from '../../../redux/slice/authSlice';
import { CAN_ACCESS_APPLICATION, EXPORTABLE_ITEMS, USER_ROLES } from '../../../constants/applicationConstants';
import { 
  fetchServiceSubscriptionList, 
  useServiceSubscriptionReducer,
  SearchServiceSubscriptionRequest,
  ServiceSubscriptionInfo
} from '../../../redux/slice/serviceSubscriptionSlice';
import { formatDate, hideElement } from '../../../utils/Utility';
import { createListSort } from '../../../utils/listSort';
import { saveSelectionForPage } from '../../../utils/StorageUtils';
import CustomMultipleDropDown from '../../common/CustomMultipleDropDown';
import { fetchDocumentByIdCommonApi, resetDocumentData } from '../../../redux/slice/commonSlice';
import PDFViewer from '../../common/pdf/PDFViewer';
import CustomIconButton from '../../common/CustomIconButton';
import ExportToExcel from '../../common/ExportToExcel';
import StyledMenu from '../../common/StyledMenu';
import { useModalStyles } from '../../common/useModalStyles';
import { useFilterPanelFocus } from "../../../hooks/useFilterPanelFocus";
// import { getActiveServices } from '../../../service/ServiceSubscriptionService';

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
}

interface SearchServiceSubscription {
  serviceType: string[];
  userName: string;
  userEmail: string;
  userStatus: string[];
  currentPage: number;
  itemsPerPage: number;
  sortBy?: SearchServiceSubscriptionRequest["sortBy"];
  sortOrder?: SearchServiceSubscriptionRequest["sortOrder"];
}

const subscriptionListSort = createListSort(
  ["userName", "userEmail", "signDatetime", "status"] as const,
  {
    userName: "userName",
    userEmail: "userEmail",
    signDatetime: "signDatetime",
    status: "status",
  }
);

const ITEMS_PER_PAGE = 20;

const ServiceSubscriptionList: React.FC = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { primaryBtnSx, tokens: modalTokens } = useModalStyles();

  const { control, handleSubmit, setValue, getValues } = useForm<SearchServiceSubscription>({
    defaultValues: {
      serviceType: [],
      userName: "",
      userEmail: "",
      userStatus: ["SIGNED"]
    }
  });
  const [showCard, setShowCard] = useState(true);
  const {
    filterPanelRef,
    toggleFilterPanel,
    handleFilterPanelEntering,
    handleFilterPanelEntered,
    handleFilterPanelExited,
  } = useFilterPanelFocus(showCard, setShowCard);
  const [activeButtonId, setActiveButtonId] = useState<string | null>(null);
  const [showPDF, setShowPDF] = useState<{ isShow: boolean; title: string }>({ isShow: false, title: "" });
  const [pdfBase64, setPDFBase64] = useState<string>("");
  const [anchorElMenu, setAnchorElMenu] = useState<null | HTMLElement>(null);
  const { userInfo } = useAuthSlice();
  const { serviceSubscriptionList } = useServiceSubscriptionReducer();
  // const [serviceTypeOptions, setServiceTypeOptions] = useState<{ label: string; value: string }[]>([]);
  
  const canAccessSubscriptionManagement = useSelector((state: RootState) => 
    selectIsValueFoundInToken("canAccessApplication", CAN_ACCESS_APPLICATION.MEDICAL_NOTE_AUDIT)(state)
  );

  const totalRecords = serviceSubscriptionList?.totalElements || 0;

  const [searchCriteria, setSearchCriteria] = useState<SearchServiceSubscription>({
    serviceType: [],
    userName: "",
    userEmail: "",
    userStatus: ["SIGNED"],
    currentPage: 1,
    itemsPerPage: ITEMS_PER_PAGE
  });

  // Load active button selection from session storage
  useEffect(() => {
    const saved = sessionStorage.getItem("activeActionButtonId");
    if (saved) {
      setActiveButtonId(saved);
    }
  }, []);

  // Status options
  const statusOptions = useMemo(() => {
    return [
      { value: "SIGNED", label: "SIGNED" },
      { value: "PENDING", label: "PENDING" },
      { value: "REVOKED", label: "REVOKED" },
    ];
  }, []);

  // Initial search on component mount
  useEffect(() => {
    searchSubscriptionList(1, searchCriteria);
  }, []);

  const searchSubscriptionList = (page: number, criteria: SearchServiceSubscription) => {
    const normalizedCriteria = subscriptionListSort.normalize(criteria);
    const searchRequest: SearchServiceSubscriptionRequest = {
      serviceTypes: normalizedCriteria.serviceType.length > 0 ? normalizedCriteria.serviceType : undefined,
      userName: normalizedCriteria.userName || undefined,
      userEmail: normalizedCriteria.userEmail || undefined,
      userStatus: (normalizedCriteria.userStatus.length > 0 && normalizedCriteria.userStatus.length < 3) ? normalizedCriteria.userStatus : undefined,
      currentPage: page,
      itemsPerPage: normalizedCriteria.itemsPerPage,
      sortBy: normalizedCriteria.sortBy,
      sortOrder: normalizedCriteria.sortOrder,
    };

    if (userInfo.userRole !== USER_ROLES.ROLE_PRACTICE_USER_PRU) {
      dispatch(fetchServiceSubscriptionList(searchRequest));
    }
    setValue("currentPage", page);
    setSearchCriteria({ ...normalizedCriteria, currentPage: page });
  };

  const onSubmit = (data: SearchServiceSubscription) => {
    const criteria = {
      ...searchCriteria,
      serviceType: data.serviceType || [],
      userName: data.userName || "",
      userEmail: data.userEmail || "",
      userStatus: data.userStatus || ["SIGNED"],
      currentPage: 1,
      ...subscriptionListSort.pick(searchCriteria),
    };
    searchSubscriptionList(1, criteria);
  };

  const handlePageChange = (pageNumber: number) => {
    searchSubscriptionList(pageNumber + 1, searchCriteria);
  };

  const handleTableSort = (columnId: string, order: "asc" | "desc") => {
    const data = subscriptionListSort.withSort(searchCriteria, columnId, order);
    if (!data) {
      return;
    }
    searchSubscriptionList(1, data);
  };

  const handleActionButtonClick = (buttonId: string, originalClick: () => void) => {
    const currentPageKey = "serviceSubscriptionList";
    saveSelectionForPage(currentPageKey, buttonId);
    setActiveButtonId(buttonId);
    sessionStorage.setItem("activeActionButtonId", buttonId);
    originalClick();
  };

  const handlePreviewDocument = async (row: ServiceSubscriptionInfo) => {
    if (row.signedDocumentId) {
      try {
        // Clear previous PDF before loading new one
        setPDFBase64("");
        setShowPDF({ isShow: false, title: "" });
        
        const result = await dispatch(fetchDocumentByIdCommonApi(row.signedDocumentId)).unwrap();
        if (result) {
          setPDFBase64(result.content);
          setShowPDF({ isShow: true, title: result.documentName || "Service_Subscription_Agreement.pdf" });
        }
      } catch (error) {
        console.error("Error fetching document:", error);
      }
    }
  };

  const handleClosePDF = () => {
    setShowPDF({ isShow: false, title: "" });
    setPDFBase64("");
    dispatch(resetDocumentData());
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenu(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorElMenu(null);
  };

  const handleExport = () => {
    const exportData = {
      ...searchCriteria,
      downloadState: true
    };

    ExportToExcel(exportData, columns, dispatch, EXPORTABLE_ITEMS.SERVICE_SUBSCRIPTION);
    handleMenuClose();
  };

  const columns: Column[] = [
    { id: "userName", label: "User Name", minWidth: 150 },
    { id: "userEmail", label: "Email", minWidth: 150 },
    { id: "signDatetime", label: "Sign Date", minWidth: 120 },
    { id: "status", label: "Status", minWidth: 100 },
    { id: "action", label: "Action", minWidth: 50 },
  ];

  const handleRowClick = (row: any) => {
    if (row.signedDocumentId) {
      handleActionButtonClick(
        `subscriptionActionButton-${row.serviceSubscriptionId}`,
        () => handlePreviewDocument(row)
      );
    }
  };

  const actionComponent = (row: ServiceSubscriptionInfo): JSX.Element => {
    return (
      <>
        <CustomIconButton
          color="info"
          size="small"
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            handleActionButtonClick(
              `subscriptionActionButton-${row.serviceSubscriptionId}`,
              () => handlePreviewDocument(row)
            );
          }}
          tooltipMessage="PDF"
          tooltipPlacement="top"
          style={{ display: `${hideElement(!row.signedDocumentId)}` }}
        >
          <PictureAsPdfOutlined fontSize="small" />
        </CustomIconButton>
      </>
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "SIGNED":
        return "#1aa457"; // green
      case "REVOKED":
        return "#cb0000"; // red
      case "PENDING":
        return "#e0a802"; // orange
      default:
        return "#757575"; // grey
    }
  };

  const formattedSubscriptionRows = useMemo(() => {
    let formatRows: any[] = [];
    serviceSubscriptionList?.content?.forEach((row: ServiceSubscriptionInfo) => {
      let formatRow = {
        ...row,
        userName: row.userName || "-",
        userEmail: row.userEmail || "-",
        signDatetime: row.signDatetime ? formatDate(row.signDatetime) : "-",
        rawStatus: row.status,
        status: (
          <Typography
            variant="body2"
            sx={{
              color: `${getStatusColor(row.status)} !important`,
            }}
          >
            {row.status}
          </Typography>
        ),
      };
      formatRows.push(formatRow);
    });
    return formatRows;
  }, [serviceSubscriptionList]);

  // -- Design tokens --------------------------------------------------------
  const sslBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
  const sslPanelBg     = isDark ? "#141720" : "rgba(255,255,255,0.82)";
  const sslHoverBg     = isDark ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)";

  const exportBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#14B8A6",
    borderColor: "rgba(20,184,166,0.55)",
    backgroundColor: theme.palette.mode === "dark" ? "rgba(20,184,166,0.08)" : "rgba(20,184,166,0.04)",
    whiteSpace: "nowrap",
    "& .MuiSvgIcon-root": {
      color: "#14B8A6",
    },
    "&:hover": {
      color: "#fff",
      borderColor: "#14B8A6",
      backgroundColor: "#14B8A6",
      boxShadow: "0 6px 22px rgba(20,184,166,0.28)",
      "& .MuiSvgIcon-root": {
        color: "#fff",
      },
    },
  };

  const sslCardSx = {
    borderRadius: "14px",
    border: `1px solid ${sslBorderColor}`,
    backgroundColor: sslPanelBg,
    backgroundImage: "none",
    backdropFilter: "blur(8px)",
    boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.28)" : "0 12px 28px rgba(15,23,42,0.08)",
  };

  const filterPanelSx = {
    ...sslCardSx,
    p: { xs: "14px !important", md: "18px !important" },
    "& .MuiAutocomplete-root, & .MuiFormControl-root": {
      width: "100%",
    },
    "& .MuiTextField-root": {
      height: "42px",
      minHeight: "42px !important",
    },
    "& .MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiAutocomplete-inputRoot, & .MuiAutocomplete-root .MuiOutlinedInput-root": {
      minHeight: "42px !important",
      height: "42px !important",
      borderRadius: "10px !important",
      backgroundColor: `${modalTokens.inputBg} !important`,
      color: modalTokens.textPrimary,
      fontSize: "0.88rem",
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
      alignItems: "center",
      boxSizing: "border-box",
    },
    "& .MuiAutocomplete-root .MuiOutlinedInput-root": {
      padding: "0 38px 0 12px !important",
    },
    "& .MuiAutocomplete-root .MuiInputBase-input, & .MuiAutocomplete-input, & .MuiOutlinedInput-input": {
      paddingTop: "8px !important",
      paddingBottom: "8px !important",
      height: "24px !important",
      lineHeight: "24px !important",
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-input": {
      paddingLeft: "12px !important",
      paddingRight: "12px !important",
    },
    "& .MuiAutocomplete-endAdornment": {
      top: "50% !important",
      transform: "translateY(-50%)",
      right: "8px !important",
    },
    "& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root fieldset": {
      borderColor: `${modalTokens.inputBorder} !important`,
    },
    "& .MuiOutlinedInput-root:hover fieldset, & .MuiOutlinedInput-root.Mui-focused fieldset": {
      borderColor: `${modalTokens.accent} !important`,
      borderWidth: "1px !important",
    },
    "& .MuiOutlinedInput-root.Mui-focused": {
      boxShadow: isDark ? "none" : "0 0 0 3px rgba(20,184,166,0.12)",
    },
    "& .MuiInputBase-input": {
      color: modalTokens.textPrimary,
      fontSize: "0.88rem",
    },
    "& input::selection": {
      color: modalTokens.textPrimary,
      backgroundColor: isDark ? "rgba(20,184,166,0.28)" : "rgba(20,184,166,0.2)",
    },
    "& .MuiOutlinedInput-root:has(input:-webkit-autofill)": {
      backgroundColor: "#376c9f !important",
      "& fieldset": { borderColor: "#7fb1df !important" },
    },
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
      WebkitTextFillColor: "#ffffff",
      caretColor: "#ffffff",
      WebkitBoxShadow: "0 0 0 100px #376c9f inset",
      borderRadius: "10px",
      transition: "background-color 5000s ease-in-out 0s",
    },
    "& .MuiInputLabel-root": { color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)", fontSize: "0.88rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: `${modalTokens.accent} !important` },
    "& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": { transform: "translate(14px, 9px) scale(1)" },
    "& .MuiChip-root": {
      borderRadius: "999px",
      backgroundColor: isDark ? "rgba(20,184,166,0.16)" : "rgba(20,184,166,0.1)",
      color: modalTokens.accent,
      border: `1px solid ${isDark ? "rgba(20,184,166,0.26)" : "rgba(20,184,166,0.18)"}`,
      fontWeight: 600,
    },
  };

  const filterFieldLabelSx = {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: modalTokens.textSecondary,
    mb: 0.7,
    height: "16px",
    lineHeight: "16px",
  };

  const primaryActionBtnSx = {
    ...primaryBtnSx,
    color: "#fff",
    "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
    "& .MuiButton-startIcon": { color: "#fff" },
  };

  const outlineBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#00B8D9",
    borderColor: "rgba(0,184,217,0.55)",
    backgroundColor: isDark ? "rgba(0,184,217,0.08)" : "rgba(0,184,217,0.04)",
    whiteSpace: "nowrap",
    "&:hover": {
      color: "#fff",
      borderColor: "#00B8D9",
      backgroundColor: "#00B8D9",
      boxShadow: "0 6px 22px rgba(0,184,217,0.28)",
    },
  };

  const exportMenuItemBtnSx = {
    justifyContent: "flex-start",
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "8px",
    py: 1,
    px: 1.4,
    color: "#2dd4bf",
    borderColor: "rgba(45,212,191,0.35)",
    backgroundColor: isDark ? "rgba(20,184,166,0.13)" : "rgba(20,184,166,0.08)",
    boxShadow: "none",
    "& .MuiButton-startIcon": {
      mr: 1,
      "& svg": { fontSize: "18px" },
    },
    "&:hover": {
      color: "#fff",
      borderColor: "#2f9b8f",
      backgroundColor: "#2f9b8f",
      boxShadow: "0 10px 24px rgba(20,184,166,0.28)",
    },
    "&.Mui-disabled": {
      color: isDark ? "rgba(148,163,184,0.55)" : "rgba(100,116,139,0.7)",
      borderColor: isDark ? "rgba(148,163,184,0.18)" : "rgba(100,116,139,0.24)",
      backgroundColor: isDark ? "rgba(148,163,184,0.06)" : "rgba(100,116,139,0.06)",
    },
  };

  const sslTableSx = {
    "& .MuiTableContainer-root": {
      borderRadius: "12px",
      border: "none",
      backgroundColor: "transparent",
    },
    "& table": {
      
      borderCollapse: "collapse",
      borderSpacing: 0,
    },
    "& .MuiTableCell-root": {
      borderBottom: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
      py: 1.15,
      fontSize: "0.84rem",
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      height: "42px",
    },
    "& .MuiTableHead-root .MuiTypography-root": {
      color: "#00B8D9 !important",
      fontSize: "0.78rem",
      fontWeight: 800,
      letterSpacing: "0.02em",
    },
    "& .MuiTableBody-root .MuiTableRow-root": {
      transition: "background-color 0.18s ease, box-shadow 0.18s ease",
    },
    "& .MuiTableBody-root .MuiTableRow-root:hover": {
      backgroundColor: `${sslHoverBg} !important`,
    },
    "& .MuiTableBody-root .MuiTableCell-root, & .MuiTableBody-root .MuiTypography-root": {
      color: modalTokens.textPrimary,
      fontSize: "0.84rem",
    },
    "& .MuiIconButton-root": {
      color: isDark ? "#94a3b8" : "#475569",
      borderRadius: "8px",
      "&:hover": {
        color: modalTokens.accent,
        backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)",
      },
    },
    "& .MuiPaginationItem-root": {
      borderColor: sslBorderColor,
      color: modalTokens.textSecondary,
      borderRadius: "8px",
      "&.Mui-selected": {
        color: "#fff",
        backgroundColor: modalTokens.accent,
        borderColor: modalTokens.accent,
      },
    },
  };
  // -- End design tokens ----------------------------------------------------

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          px: { xs: 1, md: 3 },
          py: 2,
        }}
      >
        <Box>
          <Collapse in={showCard} ref={filterPanelRef} onEntering={handleFilterPanelEntering} onEntered={handleFilterPanelEntered} onExited={handleFilterPanelExited}>
            <Card sx={filterPanelSx}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} sm={3}>
                    <CustomTextField
                      controllerName="userName"
                      label="User Name"
                      placeholder="Enter user name"
                      control={control}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <CustomTextField
                      controllerName="userEmail"
                      label="Email"
                      placeholder="Enter email"
                      control={control}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <CustomMultipleDropDown
                      name="userStatus"
                      label="Status"
                      placeholderText="Select status"
                      control={control}
                      options={statusOptions}
                      size="small"
                    />
                  </Grid>
                  <Grid item md={2} xs={12}>
                    <Button
                      fullWidth
                      size="large"
                      startIcon={<SearchOutlined />}
                      sx={{ ...primaryActionBtnSx, height: "42px", py: 0 }}
                      type="submit"
                      removeUpperCase
                    >
                      Search
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Card>
          </Collapse>
        </Box>

        <Box mt={2}>
          <Card sx={{ ...sslCardSx, p: { xs: "14px !important", md: "18px !important" } }}>
            <Grid container justifyContent="space-between" alignItems="center" spacing={1.2}>
              <Grid item xs={12} md>
                <Typography variant='h3' fontWeight={800} color="text.primary">Medical Note Audit Agreement List</Typography>
              </Grid>
              <Grid item xs={12} md="auto">
                <Button
                  variant="outlined"
                  color="info"
                  onClick={toggleFilterPanel}
                  startIcon={<SearchOutlined />}
                  fullWidth
                  sx={outlineBtnSx}
                  removeUpperCase
                >
                  {showCard ? "Hide Filters" : "Show Filters"}
                </Button>
              </Grid>
              {canAccessSubscriptionManagement && (
                <Grid item xs={12} md="auto">
                  {!isMobile && (
                    <Button
                      id="export-button"
                      aria-controls={Boolean(anchorElMenu) ? "export-menu" : undefined}
                      aria-haspopup="true"
                      aria-expanded={Boolean(anchorElMenu) ? "true" : undefined}
                      variant="outlined"
                      disableElevation
                      onClick={handleMenuClick}
                      startIcon={<ExitToApp />}
                      sx={exportBtnSx}
                      removeUpperCase
                      endIcon={
                        <Box
                          component="span"
                          sx={{
                            display: "flex",
                            transition: "transform 0.3s ease",
                            transform: anchorElMenu ? "rotate(180deg)" : "rotate(0deg)",
                            padding: "2px",
                          }}
                        >
                          <KeyboardArrowDown />
                        </Box>
                      }
                    >
                      Export
                    </Button>
                  )}
                </Grid>
              )}
              <Grid item xs={12} md="auto">
                <Box>
                  {canAccessSubscriptionManagement && isMobile && (
                    <Box mt={1}>
                      <Button
                        color="success"
                        variant="text"
                        onClick={(event) => handleMenuClick(event)}
                        startIcon={<DownloadOutlined fontSize="small" />}
                        sx={exportBtnSx}
                        removeUpperCase
                      >
                        Export
                      </Button>
                    </Box>
                  )}

                  <StyledMenu
                    id="export-menu"
                    MenuListProps={{ "aria-labelledby": "export-button" }}
                    anchorEl={anchorElMenu}
                    open={Boolean(anchorElMenu)}
                    onClose={handleMenuClose}
                    slotProps={{
                      paper: {
                        sx: {
                          width: "260px",
                          maxWidth: "90vw",
                          mt: 0.75,
                          p: 0.75,
                          borderRadius: "12px",
                          border: `1px solid ${isDark ? "rgba(0,184,217,0.3)" : "rgba(0,184,217,0.22)"}`,
                          backgroundColor: isDark ? "#141720" : "#ffffff",
                          backgroundImage: "none",
                          boxShadow: isDark
                            ? "0 18px 42px rgba(0,0,0,0.45)"
                            : "0 14px 32px rgba(15,23,42,0.14)",
                        },
                      },
                    }}
                  >
                    <Box sx={{ px: 1, mb: 1 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<CloudUpload />}
                        onClick={handleExport}
                        disabled={totalRecords > 1000}
                        sx={exportMenuItemBtnSx}
                        removeUpperCase
                      >
                        Export Search List
                      </Button>
                    </Box>

                    {totalRecords > 1000 && (
                      <Box
                        sx={{
                          mx: 1,
                          p: 1.5,
                          mt: 1,
                          backgroundColor: "warning.light",
                          borderRadius: 1,
                          color: "warning.contrastText",
                        }}
                      >
                        <Typography variant="body2">
                          Export is available only when the list contains 1,000 records or fewer.
                          Maximum export size: 1000 records.
                        </Typography>
                      </Box>
                    )}
                  </StyledMenu>
                </Box>
              </Grid>
            </Grid>

            <Table
              columns={columns}
              rows={formattedSubscriptionRows}
              uniqueValue="serviceSubscriptionId"
              totalPages={serviceSubscriptionList?.totalPages || 0}
              currentPageNumber={getValues("currentPage") ? getValues("currentPage") - 1 : 0}
              pageNumber={handlePageChange}
              activeButtonId={activeButtonId}
              totalElementText={"Records: " + (serviceSubscriptionList?.totalElements || 0)}
              emptyText="No Service Subscriptions Found"
              showMenu={true}
              sortableColumns={subscriptionListSort.sortableColumns}
              serverSideSorting
              onSortChange={handleTableSort}
              controlledSortColumn={subscriptionListSort.controlledColumn(searchCriteria.sortBy)}
              controlledSortOrder={searchCriteria.sortOrder ?? null}
              actionComponent={actionComponent}
              currentPageKey="serviceSubscriptionList"
              onRowClick={handleRowClick}
              sx={sslTableSx}
            />
          </Card>
        </Box>
      </Box>

      {/* PDF Viewer */}
      <PDFViewer
        isOpen={showPDF.isShow}
        onClose={handleClosePDF}
        base64String={pdfBase64}
        title={showPDF.title}
      />
    </>
  );
};

export default ServiceSubscriptionList;